import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, 
  Search, 
  MessageCircle, 
  User, 
  Menu, 
  X, 
  Bell,
  BookOpen,
  Users,
  Compass,
  Plus,
  Settings,
  LogOut,
  Heart,
  Sparkles,
  Video,
  ShoppingBag,
  Church,
  Music,
  Calendar,
  Award,
  Shield,
  Globe,
  Bookmark,
  ChevronRight
} from 'lucide-react';
import { useUI } from '@/contexts/UIContext';
import { getMediaUrl } from '@/utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// New DharmBook Logo Component
const DharmBookLogo = ({ size = 'md' }) => {
  const sizes = {
    sm: 'w-9 h-9',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  return (
    <div className={`${sizes[size]} rounded-xl overflow-hidden shadow-md`}>
      <img 
        src="/dharmbook-logo.png" 
        alt="DharmBook"
        className="w-full h-full object-cover scale-110"
      />
    </div>
  );
};

export const DharmBookLayout = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const { hideBottomNav } = useUI();

  // Detect if user is in an active chat conversation (full-screen mode)
  const isFullScreenChat = /^\/messages\/.+/.test(location.pathname);

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('dharmbook_token');
        if (!token) return;
        
        const response = await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadNotifications(response.data.count || 0);
      } catch (err) {
        console.error('Error fetching notification count:', err);
      }
    };

    fetchUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Bottom navigation items - ORIGINAL: Home, Granth, Flow, Shop, Jyotish
  const bottomNavItems = [
    { icon: Home, path: '/', label: 'Home' },
    { icon: BookOpen, path: '/ai-granth', label: 'Granth' },
    { icon: Video, path: '/flow', label: 'Flow' },
    { icon: ShoppingBag, path: '/shop', label: 'Shop' },
    { icon: Sparkles, path: '/jyotish', label: 'Jyotish' },
  ];

  // Sidebar menu items
  const menuItems = [
    { icon: Home, label: 'Home', path: '/', color: 'text-orange-600' },
    { icon: User, label: 'My Profile', path: '/profile', color: 'text-blue-600' },
    { icon: Compass, label: 'Discover Devotees', path: '/discover', color: 'text-teal-600' },
    { icon: BookOpen, label: 'AI Granth', path: '/ai-granth', color: 'text-amber-700' },
    { icon: Sparkles, label: 'VedVaani (Jyotish)', path: '/jyotish', color: 'text-purple-600' },
    { icon: Video, label: 'Flow (Pravah)', path: '/flow', color: 'text-rose-500' },
    { icon: Users, label: 'Find Pandit Ji', path: '/pandit', color: 'text-orange-600' },
    { icon: Calendar, label: 'My Pooja Bookings', path: '/pandit/bookings', color: 'text-purple-600' },
    { icon: Heart, label: 'DharmVivah', path: '/vivah', color: 'text-rose-600' },
    { icon: ShoppingBag, label: 'DharmShop', path: '/shop', color: 'text-emerald-600' },
    { icon: Church, label: 'Live Darshan', path: '/live', color: 'text-red-600' },
    { icon: Music, label: 'Bhakti Sangeet', path: '/music', color: 'text-indigo-600' },
    { icon: MessageCircle, label: 'Messages', path: '/messages', color: 'text-blue-600' },
    { icon: Bookmark, label: 'Saved', path: '/saved', color: 'text-amber-600' },
    { icon: Settings, label: 'Settings', path: '/settings', color: 'text-slate-600' },
  ];

  // Add role-specific items
  if (user?.role === 'pandit') {
    menuItems.splice(6, 0, { icon: Award, label: 'Pandit Dashboard', path: '/pandit/dashboard', color: 'text-amber-600' });
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item) => {
    if (item.isCreate) {
      // Navigate to create post page
      navigate('/create-post');
      return;
    }
    navigate(item.path);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] w-full max-w-[100vw] overflow-x-hidden">
      {/* Top Navigation Bar - Hidden in full-screen chat */}
      {!isFullScreenChat && (
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-stone-200 z-50 h-14 shadow-sm">
        <div className="flex items-center justify-between h-full px-4 lg:px-8 max-w-[1400px] mx-auto w-full">
          {/* Mobile: Left - Logo Icon Only */}
          <button 
            onClick={() => navigate('/')} 
            className="lg:hidden flex items-center hover:opacity-80 transition-opacity"
            data-testid="mobile-logo-btn"
          >
            <DharmBookLogo size="sm" />
          </button>

          {/* Mobile: Center - DharmBook Text Logo (Instagram-style) */}
          <div className="lg:hidden absolute left-1/2 -translate-x-1/2">
            <span 
              className="text-[26px] text-orange-600"
              style={{ fontFamily: "'Yatra One', serif", letterSpacing: '0.01em' }}
              data-testid="mobile-text-logo"
            >
              DharmBook
            </span>
          </div>
          
          {/* Desktop: Left - Logo + Brand */}
          <button 
            onClick={() => navigate('/')} 
            className="hidden lg:flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="logo-btn"
          >
            <DharmBookLogo size="sm" />
            <span 
              className="text-2xl text-orange-600"
              style={{ fontFamily: "'Yatra One', serif", letterSpacing: '0.01em' }}
            >
              DharmBook
            </span>
          </button>

          {/* Center: Desktop Navigation with Text Labels */}
          <nav className="hidden lg:flex items-center gap-1">
            {[
              { icon: Home, path: '/', label: 'Home' },
              { icon: Compass, path: '/discover', label: 'Discover Devotees' },
              { icon: BookOpen, path: '/ai-granth', label: 'Scriptures' },
              { icon: Users, path: '/pandit', label: 'Pandit Ji' },
              { icon: Church, path: '/live', label: 'Temples' },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                  isActive(item.path)
                    ? 'bg-orange-100 text-orange-600 font-medium'
                    : 'text-slate-600 hover:bg-orange-50 hover:text-orange-600'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" strokeWidth={2} />
                <span className="text-sm">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Right: Search Bar + Actions */}
          <div className="flex items-center gap-2">
            {/* Desktop Search Bar */}
            <div className="hidden lg:block">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-56 pl-10 pr-4 py-2 bg-stone-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:bg-white transition-all"
                />
              </form>
            </div>

            {/* Notifications */}
            <button
              onClick={() => navigate('/notifications')}
              className="p-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-full transition-colors relative"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" strokeWidth={1.5} />
              {unreadNotifications > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold px-1">
                  {unreadNotifications > 99 ? '99+' : unreadNotifications}
                </span>
              )}
            </button>

            {/* Profile Avatar / Menu Toggle */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="w-9 h-9 rounded-full overflow-hidden border-2 border-orange-200 hover:border-orange-400 transition-colors"
              data-testid="profile-menu-btn"
            >
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-orange-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar - Expandable */}
        {showSearch && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-stone-200 p-3 shadow-lg">
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search scriptures, devotees, posts..."
                  className="w-full pl-12 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
                  autoFocus
                  data-testid="search-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSearch(false)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        )}
      </header>
      )}

      {/* Mobile Slide Menu - Hidden in full-screen chat */}
      {showMobileMenu && !isFullScreenChat && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setShowMobileMenu(false)} />
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] z-50 shadow-2xl flex flex-col overflow-hidden" style={{ background: '#FFF8EE' }}>
            
            {/* Header — DharmBook branding + close */}
            <div className="flex-shrink-0 px-5 pt-5 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DharmBookLogo size="sm" />
                <span className="text-xl text-[#5D3A1A]" style={{ fontFamily: "'Yatra One', serif" }}>DharmBook</span>
              </div>
              <button onClick={() => setShowMobileMenu(false)} className="w-8 h-8 rounded-full bg-[#F0E0C8] flex items-center justify-center hover:bg-[#E5D0B0] transition-colors" data-testid="close-menu-btn">
                <X className="w-4 h-4 text-[#5D3A1A]" />
              </button>
            </div>

            {/* Profile Card */}
            <button
              onClick={() => { navigate('/profile'); setShowMobileMenu(false); }}
              className="mx-5 mb-4 flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm border border-[#F0E0C8] hover:shadow-md transition-all"
              data-testid="menu-profile-link"
            >
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#E5A44C] flex-shrink-0">
                {user?.avatar ? (
                  <img src={getMediaUrl(user.avatar)} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-200 to-orange-300 flex items-center justify-center">
                    <User className="w-6 h-6 text-[#5D3A1A]" />
                  </div>
                )}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="font-bold text-[#5D3A1A] text-[15px] truncate">{user?.name || 'Guest'}</p>
                <p className="text-xs text-[#A08060]">Seeker of Dharma</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#C0A080] flex-shrink-0" />
            </button>

            {/* Scrollable Menu Content */}
            <div className="flex-1 overflow-y-auto px-5 pb-6 scrollbar-hide">
              
              {/* MY SPACE */}
              <p className="text-[10px] font-bold text-[#B08050] tracking-widest uppercase mb-2">My Space</p>
              <div className="bg-white rounded-2xl shadow-sm border border-[#F0E0C8] mb-5 overflow-hidden">
                {[
                  { icon: User, label: 'Profile', path: '/profile', color: '#E5A44C' },
                  { icon: Bookmark, label: 'Saved', path: '/saved', color: '#D4890A' },
                  { icon: Calendar, label: 'Bookings', path: '/pandit/bookings', color: '#C06020' },
                  { icon: Settings, label: 'Settings', path: '/settings', color: '#8B6040' },
                ].map((item, i, arr) => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FFF5E8] transition-colors ${i < arr.length - 1 ? 'border-b border-[#F8ECD8]' : ''}`}
                    data-testid={`menu-${item.label.toLowerCase()}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${item.color}18` }}>
                      <item.icon className="w-4 h-4" style={{ color: item.color }} strokeWidth={1.8} />
                    </div>
                    <span className="flex-1 text-left text-sm font-medium text-[#5D3A1A]">{item.label}</span>
                    <ChevronRight className="w-4 h-4 text-[#D0B890]" />
                  </button>
                ))}
              </div>

              {/* SPIRITUAL */}
              <p className="text-[10px] font-bold text-[#B08050] tracking-widest uppercase mb-2">Spiritual</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {[
                  { icon: '🪔', label: 'Daily Dharma', path: '/' },
                  { icon: '📖', label: 'Granth', path: '/ai-granth' },
                  { icon: '🔴', label: 'Live Aarti', path: '/live' },
                  { icon: '🎵', label: 'Bhakti', path: '/music' },
                ].map(item => (
                  <button
                    key={item.path + item.label}
                    onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
                    className="flex flex-col items-center gap-1.5 py-4 bg-white rounded-2xl shadow-sm border border-[#F0E0C8] hover:shadow-md hover:border-[#E5D0B0] transition-all"
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-[11px] font-semibold text-[#5D3A1A]">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* COMMUNITY */}
              <p className="text-[10px] font-bold text-[#B08050] tracking-widest uppercase mb-2">Community</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {[
                  { icon: '🌊', label: 'Dharm Flow', path: '/flow' },
                  { icon: '🧘', label: 'Satsang', path: '/discover' },
                  { icon: '🔍', label: 'Discover', path: '/discover' },
                  { icon: '🤝', label: 'Friends', path: '/discover' },
                ].map((item, i) => (
                  <button
                    key={item.label + i}
                    onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
                    className="flex flex-col items-center gap-1.5 py-4 bg-white rounded-2xl shadow-sm border border-[#F0E0C8] hover:shadow-md hover:border-[#E5D0B0] transition-all"
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-[11px] font-semibold text-[#5D3A1A]">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* SERVICES */}
              <p className="text-[10px] font-bold text-[#B08050] tracking-widest uppercase mb-2">Services</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {[
                  { icon: '🙏', label: 'Pandit Ji', path: '/pandit' },
                  { icon: '⭐', label: 'Jyotish', path: '/jyotish' },
                  { icon: '💍', label: 'DharmVivah', path: '/vivah' },
                  { icon: '🛍️', label: 'DharmShop', path: '/shop' },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
                    className="flex flex-col items-center gap-1.5 py-4 bg-white rounded-2xl shadow-sm border border-[#F0E0C8] hover:shadow-md hover:border-[#E5D0B0] transition-all"
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-[11px] font-semibold text-[#5D3A1A]">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Admin + Pandit Dashboard */}
              {user?.role === 'pandit' && (
                <button
                  onClick={() => { navigate('/pandit/dashboard'); setShowMobileMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-white rounded-2xl shadow-sm border border-[#F0E0C8] mb-3 hover:shadow-md transition-all"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Award className="w-4 h-4 text-amber-600" strokeWidth={1.8} />
                  </div>
                  <span className="text-sm font-medium text-[#5D3A1A]">Pandit Dashboard</span>
                </button>
              )}
              <button
                onClick={() => { navigate('/admin/login'); setShowMobileMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-white rounded-2xl shadow-sm border border-[#F0E0C8] mb-5 hover:shadow-md transition-all"
              >
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-red-500" strokeWidth={1.8} />
                </div>
                <span className="text-sm font-medium text-[#5D3A1A]">Admin Panel</span>
              </button>

              {/* Logout */}
              <button
                onClick={() => { onLogout?.(); setShowMobileMenu(false); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-200 bg-red-50/50 text-red-600 hover:bg-red-100 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.8} />
                <span className="text-sm font-semibold">Logout</span>
              </button>

              {/* Bottom safe area for nav bar */}
              <div className="h-24" />
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className={isFullScreenChat ? 'h-screen' : 'pt-14 pb-20 min-h-screen'}>
        <Outlet />
      </main>

      {/* Bottom Navigation - Mobile - Hidden in full-screen chat */}
      {!hideBottomNav && !isFullScreenChat && (
        <nav 
          className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
          data-testid="bottom-nav"
        >
          {/* Warm cream background with subtle top border */}
          <div className="bg-white border-t border-[#F5E6D0]">
            <div className="flex items-center justify-around h-16 px-1 relative">
              {/* Home — Mandir/Temple */}
              <button
                onClick={() => navigate('/')}
                className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-2 transition-all duration-200 active:scale-110"
                data-testid="nav-home"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="transition-all duration-200">
                  {/* Temple dome */}
                  <path d="M12 2C12 2 8 6 8 8v2H4l1 10h14l1-10h-4V8c0-2-4-6-4-6z" stroke={location.pathname === '/' ? '#D4550A' : '#B0A899'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill={location.pathname === '/' ? '#FFF0E0' : 'none'} />
                  {/* Temple flag */}
                  <line x1="12" y1="2" x2="12" y2="5" stroke={location.pathname === '/' ? '#D4550A' : '#B0A899'} strokeWidth="1.6" />
                  {/* Door */}
                  <rect x="10" y="14" width="4" height="6" rx="2" stroke={location.pathname === '/' ? '#D4550A' : '#B0A899'} strokeWidth="1.2" fill="none" />
                </svg>
                <span className={`text-[10px] font-semibold transition-colors duration-200 ${location.pathname === '/' ? 'text-[#D4550A]' : 'text-[#B0A899]'}`}>Home</span>
              </button>

              {/* Granth — Sacred Book */}
              <button
                onClick={() => navigate('/ai-granth')}
                className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-2 transition-all duration-200 active:scale-110"
                data-testid="nav-granth"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="transition-all duration-200">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke={location.pathname.startsWith('/ai-granth') ? '#D4550A' : '#B0A899'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke={location.pathname.startsWith('/ai-granth') ? '#D4550A' : '#B0A899'} strokeWidth="1.6" fill={location.pathname.startsWith('/ai-granth') ? '#FFF0E0' : 'none'} />
                  {/* Om on book */}
                  <text x="12" y="13.5" textAnchor="middle" fontSize="8" fontWeight="bold" fill={location.pathname.startsWith('/ai-granth') ? '#D4550A' : '#B0A899'} style={{fontFamily: 'serif'}}>Om</text>
                </svg>
                <span className={`text-[10px] font-semibold transition-colors duration-200 ${location.pathname.startsWith('/ai-granth') ? 'text-[#D4550A]' : 'text-[#B0A899]'}`}>Granth</span>
              </button>

              {/* Center — Om FAB with divine glow */}
              <div className="relative -mt-7">
                {/* Outer glow ring */}
                <div className="absolute inset-0 -m-1 rounded-full opacity-40" style={{ background: 'radial-gradient(circle, rgba(255,153,51,0.4) 0%, transparent 70%)' }} />
                <button
                  onClick={() => navigate('/flow')}
                  className="relative w-14 h-14 aspect-square rounded-full flex items-center justify-center transition-all duration-300 active:scale-90 hover:scale-105 overflow-hidden"
                  style={{
                    background: 'linear-gradient(145deg, #FF8C00 0%, #E85D04 50%, #DC2F02 100%)',
                    boxShadow: '0 4px 16px rgba(232,93,4,0.4), 0 1px 3px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
                    minWidth: '56px',
                    maxWidth: '56px',
                    minHeight: '56px',
                    maxHeight: '56px',
                  }}
                  data-testid="nav-flow"
                >
                  {/* Om Symbol */}
                  <span className="text-white text-[22px] leading-none" style={{ fontFamily: "'Yatra One', serif" }}>
                    ॐ
                  </span>
                </button>
                <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[10px] font-bold text-[#D4550A] whitespace-nowrap">Flow</span>
              </div>

              {/* Messages — Chat/Conch */}
              <button
                onClick={() => navigate('/messages')}
                className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-2 transition-all duration-200 active:scale-110"
                data-testid="nav-messages"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="transition-all duration-200">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke={location.pathname.startsWith('/messages') ? '#D4550A' : '#B0A899'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill={location.pathname.startsWith('/messages') ? '#FFF0E0' : 'none'} />
                </svg>
                <span className={`text-[10px] font-semibold transition-colors duration-200 ${location.pathname.startsWith('/messages') ? 'text-[#D4550A]' : 'text-[#B0A899]'}`}>Messages</span>
              </button>

              {/* Shop — Kalash/Pot */}
              <button
                onClick={() => navigate('/shop')}
                className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] py-2 transition-all duration-200 active:scale-110"
                data-testid="nav-shop"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="transition-all duration-200">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" stroke={location.pathname.startsWith('/shop') ? '#D4550A' : '#B0A899'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill={location.pathname.startsWith('/shop') ? '#FFF0E0' : 'none'} />
                  <line x1="3" y1="6" x2="21" y2="6" stroke={location.pathname.startsWith('/shop') ? '#D4550A' : '#B0A899'} strokeWidth="1.6" />
                  <path d="M16 10a4 4 0 0 1-8 0" stroke={location.pathname.startsWith('/shop') ? '#D4550A' : '#B0A899'} strokeWidth="1.6" />
                </svg>
                <span className={`text-[10px] font-semibold transition-colors duration-200 ${location.pathname.startsWith('/shop') ? 'text-[#D4550A]' : 'text-[#B0A899]'}`}>Shop</span>
              </button>
            </div>
          </div>
        </nav>
      )}
    </div>
  );
};

export default DharmBookLayout;
